import escapeRegExp from "lodash/escapeRegExp";
import { CustomCategories } from "@app/constants";

export const UNCATEGORIZED = { _id: CustomCategories.UNCATEGORIZED, name: "Uncategorized" };

export const searchTokens = (text) => {
    return String(text ?? "")
        .split(/\s+/g)
        .filter((token) => !!token)
        .map((token) => token.toLowerCase());
};

/**
 * Group the documents by category, unknown categories go to "Uncategorized"
 */
export const groupDocuments = (documents = [], categories = []) => {
    const groups = categories.map((category) => ({
        _id: String(category._id),
        name: category.name ?? "",
        documents: [],
    }));
    const byId = new Map(groups.map((group) => [group._id, group]));
    const uncategorized = { ...UNCATEGORIZED, documents: [] };

    documents.forEach((document) => {
        const ids = [...new Set((document.categories ?? []).map(String))].filter((id) =>
            byId.has(id),
        );
        const entry = { ...document, categories: ids };

        if (ids.length === 0) {
            uncategorized.documents.push(entry);
        } else {
            ids.forEach((id) => byId.get(id).documents.push(entry));
        }
    });

    return [...groups, uncategorized].filter((group) => group.documents.length > 0);
};

/**
 * Filter the groups by category and the documents by search tokens (document or category name)
 */
export const filterGroups = (groups = [], { tokens = [], categoryIds = [] } = {}) => {
    const picked = new Set(categoryIds.map(String));

    return groups
        .filter((group) => picked.size === 0 || picked.has(group._id))
        .map((group) => {
            if (tokens.length === 0) {
                return group;
            }

            const groupName = group.name.toLowerCase();
            const documents = group.documents.filter((document) => {
                const name = (document.name ?? "").toLowerCase();
                return tokens.every((token) => name.includes(token) || groupName.includes(token));
            });

            return { ...group, documents };
        })
        .filter((group) => group.documents.length > 0);
};

/**
 * Distinct documents of the groups (a document can be listed in several groups)
 */
export const uniqueDocuments = (groups = []) => {
    const seen = new Map();

    groups.forEach((group) => {
        group.documents.forEach((document) => {
            if (!seen.has(document._id)) {
                seen.set(document._id, document);
            }
        });
    });

    return [...seen.values()];
};

/**
 * Split a text into chunks flagged with whether they match one of the search tokens
 */
export const highlightChunks = (text, tokens = []) => {
    const value = String(text ?? "");
    const patterns = [...new Set(tokens)]
        .filter((token) => !!token)
        .sort((a, b) => b.length - a.length)
        .map(escapeRegExp);

    if (!value || patterns.length === 0) {
        return value ? [{ text: value, match: false }] : [];
    }

    const regex = new RegExp(patterns.join("|"), "gi");
    const chunks = [];
    let last = 0;
    let found;

    while ((found = regex.exec(value)) !== null) {
        if (found.index > last) {
            chunks.push({ text: value.slice(last, found.index), match: false });
        }

        chunks.push({ text: found[0], match: true });
        last = found.index + found[0].length;
    }

    if (last < value.length) {
        chunks.push({ text: value.slice(last), match: false });
    }

    return chunks;
};
